import React from "react";
import { FileText, RefreshCw, AlertCircle, Globe, Clock, Target } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenKeywords, flattenMetrics, flattenTopPages } from "../../reports/exportDataUtils";

interface ContentTabContentProps {
  liveData?: any | null;
  activeSubNav?: string;
}

const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const ContentTabContent: React.FC<ContentTabContentProps> = ({ liveData, activeSubNav }) => {

  // ─── SUB-NAV: Domain Pages ───
  if (activeSubNav === "domain-pages") {
    const topPages: any[] = liveData?.topPages ?? [];
    const pageKeywords: any[] = liveData?.topPages?.flatMap((p: any) => p?.keywords || []) ?? [];

    return (
      <div className="space-y-6">
        <DataPanel title="Top Domain Pages" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Top Domain Pages" data={flattenTopPages(topPages)} />}>
          {topPages.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["URL", "Keywords", "Est. Traffic"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topPages.slice(0, 10).map((p: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2 font-mono text-xs max-w-[350px] truncate" style={{ color: "#8B7BF7" }}>{p.url}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{p.keywordsCount ?? 0}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(p.trafficSum ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
                Showing {Math.min(topPages.length, 10)} of {topPages.length} pages
              </div>
            </>
          ) : (
            <EmptyState title="No domain pages data yet" description="Run a Domain Pages audit to see your top-performing pages by traffic." icon={<Globe className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
          )}
        </DataPanel>

        <DataPanel title="Page Keywords" icon={<FileText className="w-4 h-4" />} actions={<WidgetExportBtn title="Page Keywords" data={flattenKeywords(pageKeywords)} />}>
          {pageKeywords.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Position", "Volume", "Difficulty", "Traffic Potential"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageKeywords.slice(0, 10).map((kw: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>#{kw.position ?? kw.pos ?? "—"}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(kw.searchVolume ?? kw.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(kw.difficulty ?? 0, 100)}%`, background: (kw.difficulty ?? 0) > 60 ? "#F87171" : (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                          </div>
                          <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? 0}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(kw.trafficPotential ?? kw.traffic ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No page keyword data yet" description="Run a Domain Pages audit to see keywords driving traffic to your pages." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Content Gaps ───
  const contentGaps: any[] = liveData?.competitors?.flatMap((c: any) => c.keywordGaps || []) ?? []
  const hasContentData = contentGaps.length > 0;

  const refreshOpportunities: any[] = liveData?.contentRefreshOpportunities ?? [];
  const lostKeywords = refreshOpportunities.filter((item: any) => item.gapType === 'lost');
  const keywordGaps = refreshOpportunities.filter((item: any) => item.gapType === 'missing');

  const techSEO = liveData?.technicalSEO ?? null;

  return (
    <div className="space-y-6">
      <DataPanel title="Content Gaps" icon={<FileText className="w-4 h-4" />} actions={<WidgetExportBtn title="Content Gaps" data={contentGaps?.map((g: any) => ({ Keyword: g.keyword, "Their Pos.": g.competitorPosition, "Your Pos.": g.yourPosition || "Not ranking", Volume: g.searchVolume, Difficulty: g.difficulty, Opportunity: g.opportunity || g.gapType || "MED" })) || []} />}>
        {hasContentData ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword / Topic", "Volume", "Difficulty", "Position", "Opportunity"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contentGaps.slice(0, 10).map((kw: any, i: number) => {
                    const pos = kw.yourPosition ?? kw.pos;
                    const opp = kw.opportunity;
                    const oppColor = kw.opportunity === "high" ? "#4ADE80" : opp === "medium" ? "#FF9A4A" : "#9AA0B3";
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(kw.searchVolume ?? kw.volume ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                              <div className="h-full rounded-full" style={{ width: `${kw.difficulty ?? 0}%`, background: (kw.difficulty ?? 0) > 60 ? "#F87171" : (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                            </div>
                            <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? "—"}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2" style={{ color: pos ? "#E8E4FF" : "#9AA0B3" }}>{pos ? `#${pos}` : "Not ranking"}</td> 
                        <td className="py-2.5 px-2">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: oppColor, background: `${oppColor}20` }}>
                            {opp}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(contentGaps.length, 10)} of {contentGaps.length} content gaps
            </div>
          </>
        ) : (
          <EmptyState title="No content gap data yet" description="Run a Content Gaps audit to identify missing content opportunities." />
        )}
      </DataPanel>

      <DataPanel
        title="Content Refresh Opportunities"
        icon={<RefreshCw className="w-4 h-4" />}
        actions={<WidgetExportBtn title="Content Refresh Opportunities" data={refreshOpportunities?.map((r: any) => ({ Keyword: r.keyword, URL: r.url, Volume: r.searchVolume, Opportunity: r.opportunity || r.gapType || "MED", Reason: r.reason })) || []} />}
      >
        {refreshOpportunities.length > 0 ? (
          <div className="space-y-8">

            {/* Refresh Existing Pages (Lost Keywords) */}
            {lostKeywords.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-orange-400" />
                  <h4 className="text-lg font-semibold text-white">Refresh These Pages</h4>
                  <span className="text-xs bg-orange-500/10 text-orange-400 px-3 py-0.5 rounded-full">
                    {lostKeywords.length} lost rankings
                  </span>
                </div>
                <div className="space-y-3">
                  {lostKeywords.slice(0, 8).map((item: any, i: number) => (
                    <div key={i} className="bg-[#1f1b2e] border-l-4 border-orange-500 rounded-xl p-4 hover:bg-[#2a2636]">
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-white">"{item.keyword}"</p>
                          {item.url && (
                            <a href={item.url} target="_blank" className="text-xs text-purple-400 hover:underline mt-1 block truncate">
                              {item.url}
                            </a>
                          )}
                        </div>
                        <div className="flex justify-center items-center">
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-medium uppercase tracking-wide ${item.opportunity === "high"
                              ? "bg-green-500/20 text-green-400"
                              : item.opportunity === "medium"
                                ? "bg-yellow-500/20 text-yellow-400"
                                : "bg-red-500/20 text-red-400"
                              }`}
                          >
                            {item.opportunity}
                          </span>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className="text-xl font-semibold text-orange-400">
                            {item.searchVolume?.toLocaleString() || 0}
                          </div>
                          <div className="text-xs text-gray-500">searches/mo</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{item.reason || "Lost ranking — recommended to refresh"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Content Opportunities (Keyword Gaps) */}
            {keywordGaps.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-red-400" />
                  <h4 className="text-lg font-semibold text-white">New Content Opportunities</h4>
                  <span className="text-xs bg-orange-500/10 text-orange-400 px-3 py-0.5 rounded-full">
                    {keywordGaps.length} gaps
                  </span>
                </div>
                <div className="space-y-3">
                  {keywordGaps.slice(0, 8).map((item: any, i: number) => (
                    <div key={i} className="bg-[#1f1b2e] border-l-4 border-red-500 rounded-xl p-4 hover:bg-[#2a2636]">
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-white">"{item.keyword}"</p>
                          {item.competitorDomain && (
                            <p className="text-xs text-gray-400 mt-1">
                              {item.competitorDomain} ranks #{item.competitorPosition}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-center items-center">
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-medium uppercase tracking-wide ${item.opportunity === "high"
                                ? "bg-green-500/20 text-green-400"
                                : item.opportunity === "medium"
                                  ? "bg-yellow-500/20 text-yellow-400"
                                  : "bg-orange-500/20 text-orange-400"
                              }`}
                          >
                            {item.opportunity}
                          </span>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className="text-xl font-semibold text-orange-400">
                            {item.searchVolume?.toLocaleString() || 0}
                          </div>
                          <div className="text-xs text-gray-500">searches/mo</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title="No refresh data yet"
            description="Run a Content Gaps audit to find content that needs updating and new keyword opportunities."
            icon={<RefreshCw className="w-6 h-6" style={{ color: "#8B7BF7" }} />}
          />
        )}
      </DataPanel>

      <DataPanel title="Content Quality Metrics" icon={<AlertCircle className="w-4 h-4" />} actions={<WidgetExportBtn title="Content Quality Metrics" data={techSEO ? flattenMetrics({ score: techSEO.score, contentLength: techSEO.contentLength, internalLinks: techSEO.internalLinks, externalLinks: techSEO.externalLinks, hasSchema: techSEO.hasSchema }) : []} />}>
        {techSEO ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricCard label="Technical Score" value={`${techSEO.score ?? 0}/100`} highlight />
              <MetricCard label="Content Length" value={`${(techSEO.contentLength ?? 0).toLocaleString()} chars`} />
              <MetricCard label="Internal Links" value={String(techSEO.internalLinks ?? 0)} />
              <MetricCard label="External Links" value={String(techSEO.externalLinks ?? 0)} />
              <MetricCard label="Images" value={techSEO.images ? `${techSEO.images.total} (${techSEO.images.missingAlt} missing alt)` : "—"} />
              <MetricCard label="Schema Markup" value={techSEO.hasSchema ? "Yes" : "No"} />
            </div>

            {techSEO.titleTag && (
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                <div className="text-xs mb-2" style={{ color: "#9AA0B3" }}>Title Tag ({techSEO.titleTag.length} chars)</div>
                <div className="text-sm" style={{ color: "#C4BDE0" }}>{techSEO.titleTag.content || "—"}</div>
                <div className="flex gap-2 mt-2">
                  {techSEO.titleTag.present && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80" }}>Present</span>}
                  {techSEO.titleTag.optimized && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80" }}>Optimized</span>}
                </div>
              </div>
            )}

            {techSEO.metaDescription && (
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                <div className="text-xs mb-2" style={{ color: "#9AA0B3" }}>Meta Description ({techSEO.metaDescription.length} chars)</div>
                <div className="text-sm" style={{ color: "#C4BDE0" }}>{techSEO.metaDescription.content || "—"}</div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState title="No quality data yet" description="Run a Full SEO Audit to see content quality metrics." />
        )}
      </DataPanel>
    </div>
  );
};

export default ContentTabContent;
